import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  Pagination,
  Modal,
  IconButton,
  CircularProgress,
} from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { format } from "date-fns";
import { getAllNewsletters } from "../apis/apiList/newsletterApi";
import { getAllOrganizations } from "../apis/apiList/organizationApi";
import { useApp } from "../context/AppContext";
import CloseIcon from '@mui/icons-material/Close';
import { downloadFileFromBox } from "../apis/apiList/boxAssetsApi";

const ITEMS_PER_PAGE = 10;

export default function NewsletterListPage() {
  const [search, setSearch] = useState("");
  const [orgFilter, setOrgFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [newsletters, setNewsletters] = useState([]);
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const [emailForOrg, setEmailForOrg] = useState("");
  const [openEmailModal, setOpenEmailModal] = useState(false);
  const [selectedOrgForEmail, setSelectedOrgForEmail] = useState(null);
  const [orgList, setOrgList] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const { userDetails } = useApp();

  useEffect(() => {
    async function fetchNewsletters() {
      try {
        const res = await getAllNewsletters({
          headers: { "x-access-token": userDetails.jwtToken },
        });
        setNewsletters(res?.newsletters || []);
      } catch (err) {
        console.error("Failed to fetch newsletters:", err);
      }
    }
    fetchNewsletters();
  }, []);

  useEffect(() => {
    async function fetchOrganizations() {
      try {
        setLoadingOrgs(true);
        const res = await getAllOrganizations({
          headers: { "x-access-token": userDetails.jwtToken },
        });
        setOrgList(res?.data || []);
      } catch (error) {
        console.error("Failed to fetch organizations:", error);
      } finally {
        setLoadingOrgs(false);
      }
    }
    fetchOrganizations();
  }, []);

  const orgOptions = ["All", ...orgList.map((org) => org.organizationName)];

  const filteredNewsletters = newsletters
    .filter((n) =>
      orgFilter === "All" ? true : n?.organizationId?.organizationName === orgFilter
    )
    .filter((n) =>
      n.subject.toLowerCase().includes(search.toLowerCase()) ||
      n?.organizationId?.organizationName.toLowerCase().includes(search.toLowerCase())
    );

  const paginatedNewsletters = filteredNewsletters.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const handleViewNewsletter = (newsletter) => {
    setSelectedNewsletter(newsletter);
  };

  const handleCloseModal = () => {
    setSelectedNewsletter(null);
  };

  const handleGenerateEmail = (org) => {
    if (org?._id) {
      const email = `newsletters+${org._id}@snapshotvideos.com`;
      setEmailForOrg(email);
    } else {
      setEmailForOrg("");
    }
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(emailForOrg);
  };

  const handleDownloadAttachment = async (attachment) => {
    console.log('Downloading attachment:', attachment);
    try {
      const file = await downloadFileFromBox(attachment?.boxFileId );
      console.log('file', file)
      if (file?.data) {
        // Step 2: Create a temporary <a> tag to trigger download
        const link = document.createElement('a');
        link.href = file?.data;
        link.download = ''; // Optional: specify filename
        link.target = '_blank'; // optional, for opening in new tab
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error('File URL not found');
      }
    } catch (error) {
      console.error("Failed to download attachment:", error);
    }
  };

  return (
    <Box>
      <Box
        sx={{ borderRadius: 2, px: 3, py: 2, mb: 1, display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h3" sx={{ fontWeight: "bold", color: "#222", mb: 2.5 }}>
            Newsletter List
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button variant="outlined" onClick={() => setOpenEmailModal(true)}>
              Generate Unique Email
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 3 }}>
          {/* <Autocomplete
            className="org-autocomplete"
            size="small"
            sx={{
              '& .MuiInputBase-root': {
                height: '50px !important',
              },
            }}
            options={["All", ...orgOptions]}
            value={orgFilter}
            onChange={(e, newValue) => setOrgFilter(newValue || "All")}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by Organization"
                sx={{ minWidth: 220, width: "300px" }}
              />
            )}
          /> */}
          <Autocomplete
            size="small"
            sx={{
              '& .MuiInputBase-root': {
                height: '50px !important',
              },
            }}
            options={orgOptions}
            value={orgFilter}
            onChange={(e, newValue) => setOrgFilter(newValue || "All")}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by Organization"
                sx={{ minWidth: 220, width: "300px" }}
              />
            )}
          />

          <TextField
            size="small"
            label="Search by Subject"
            value={search}
            sx={{ minWidth: 220, width: { xs: "100%", md: "400px" } }}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>

        {paginatedNewsletters.length > 0 ? (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#495967" }}>
                    <TableCell sx={{ color: "#fff" }}>Subject</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Organization</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Received At</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Attachments</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedNewsletters.map((newsletter) => (
                    <TableRow key={newsletter._id}>
                      <TableCell>{newsletter.subject}</TableCell>
                      <TableCell>{newsletter?.organizationId?.organizationName}</TableCell>
                      <TableCell>{format(new Date(newsletter.receivedAt), "PPP p")}</TableCell>
                      <TableCell>{newsletter.attachments?.length || 0} file(s)</TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleViewNewsletter(newsletter)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={Math.ceil(filteredNewsletters.length / ITEMS_PER_PAGE)}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </>
        ) : (
          <Box
            mt={6}
            textAlign="center"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="40vh"
            color="text.secondary"
          >
            <img
              src="/images/other/newsletter.png"
              alt="Newsletters Not Found"
              style={{ width: 300, height: "auto", marginBottom: 16, opacity: 0.6 }}
            />
            <Typography variant="h2" fontWeight="bold" mb={1} color="#666666">
              Newsletters Not Found
            </Typography>
          </Box>
        )}

        <Modal open={openEmailModal} onClose={() => setOpenEmailModal(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", md: 600 },
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 3,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              Generate Unique Newsletter Email
            </Typography>
            {loadingOrgs ? (
              <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
            ) : (
              <Autocomplete
                size="small"
                options={orgList}
                getOptionLabel={(opt) => opt.organizationName || ""}
                value={selectedOrgForEmail}
                onChange={(e, newVal) => {
                  setSelectedOrgForEmail(newVal);
                  handleGenerateEmail(newVal);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Organization" fullWidth />
                )}
              />
            )}
            {emailForOrg && (
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  size="small"
                  fullWidth
                  label="Generated Email"
                  value={emailForOrg}
                  InputProps={{ readOnly: true }}
                />
                <IconButton onClick={handleCopyEmail} color="primary">
                  <ContentCopyIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        </Modal>

        <Modal open={!!selectedNewsletter} onClose={handleCloseModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", md: "70%", lg: "60%" },
              maxHeight: "90vh",
              overflowY: "auto",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 3,
            }}
          >
            <IconButton
              onClick={handleCloseModal}
              sx={{ position: "absolute", top: 8, right: 8 }}
            >
              <CloseIcon />
            </IconButton>
            {selectedNewsletter && (
              <>
                <Typography variant="h5" fontWeight="bold" mb={1}>
                  Subject: {selectedNewsletter.subject}
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Organization:</strong> {selectedNewsletter.organizationId?.organizationName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Received:</strong> {format(new Date(selectedNewsletter.receivedAt), "PPP p")}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    bgcolor: "#f5f5f5",
                    borderRadius: 2,
                    p: 2,
                    mb: 3,
                    maxHeight: "300px",
                    overflowY: "auto",
                  }}
                >
                  <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
                    {selectedNewsletter.body?.text || "No content available."}
                  </Typography>
                </Box>

                {selectedNewsletter.attachments?.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium" mb={1}>
                      Attachments
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={1}>
                      {selectedNewsletter.attachments.map((att, idx) => (
                        <Button
                          key={idx}
                          href={att.boxFileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                          size="small"
                          sx={{ textTransform: "none", justifyContent: "flex-start" }}
                          onClick={() => handleDownloadAttachment(att)}
                        >
                          📎 {att.filename}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Modal>


      </Box>
    </Box>
  );
}
